/**
 * KI-Überwachung Dashboard
 * Proaktive Fristenüberwachung mit Claude: Erinnerungen, Entwürfe, Vorlagen
 */

import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import {
  Brain, Bell, FileText, BookTemplate, Play, CheckCircle, XCircle,
  Clock, AlertTriangle, Eye, Trash2, Plus, RefreshCw, ChevronDown, ChevronUp,
  Send, Edit3, Save
} from 'lucide-react';

const API = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

export default function KiUeberwachungPage() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [entwuerfe, setEntwuerfe] = useState([]);
  const [vorlagen, setVorlagen] = useState([]);
  const [erinnerungen, setErinnerungen] = useState([]);
  const [logs, setLogs] = useState([]);
  const [triggering, setTriggering] = useState(false);
  const [expandedItem, setExpandedItem] = useState(null);
  const [showNewVorlage, setShowNewVorlage] = useState(false);
  const [newVorlage, setNewVorlage] = useState({ titel: '', kategorie: '', inhalt: '', tags: '' });
  const [editingEntwurf, setEditingEntwurf] = useState(null);

  const headers = useCallback(() => {
    const token = localStorage.getItem('token');
    return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
  }, []);

  const fetchAll = useCallback(async () => {
    try {
      const [dashRes, entRes, vorRes, erinRes, logRes] = await Promise.all([
        fetch(`${API}/api/ki-ueberwachung/dashboard`, { headers: headers() }),
        fetch(`${API}/api/ki-ueberwachung/entwuerfe`, { headers: headers() }),
        fetch(`${API}/api/ki-ueberwachung/vorlagen`, { headers: headers() }),
        fetch(`${API}/api/ki-ueberwachung/erinnerungen?nur_ungelesen=false`, { headers: headers() }),
        fetch(`${API}/api/ki-ueberwachung/log?limit=10`, { headers: headers() }),
      ]);
      if (dashRes.ok) setDashboard(await dashRes.json());
      if (entRes.ok) setEntwuerfe(await entRes.json());
      if (vorRes.ok) setVorlagen(await vorRes.json());
      if (erinRes.ok) setErinnerungen(await erinRes.json());
      if (logRes.ok) setLogs(await logRes.json());
    } catch (e) { console.error(e); }
  }, [headers]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleTrigger = async () => {
    setTriggering(true);
    try {
      const r = await fetch(`${API}/api/ki-ueberwachung/trigger`, { method: 'POST', headers: headers() });
      const data = await r.json();
      if (data.success) {
        toast.success(`Überwachung ausgeführt: ${data.aufgaben_erstellt} Aufgaben, ${data.entwuerfe_erstellt} Entwürfe, ${data.erinnerungen} Erinnerungen`);
        fetchAll();
      }
    } catch (e) { toast.error('Fehler bei der Überwachung'); }
    setTriggering(false);
  };

  const handleMarkGelesen = async (id) => {
    await fetch(`${API}/api/ki-ueberwachung/erinnerungen/${id}/gelesen`, { method: 'POST', headers: headers() });
    fetchAll();
  };

  const handleAlleGelesen = async () => {
    await fetch(`${API}/api/ki-ueberwachung/erinnerungen/alle-gelesen`, { method: 'POST', headers: headers() });
    fetchAll();
    toast.success('Alle Erinnerungen als gelesen markiert');
  };

  const handleFreigeben = async (id) => {
    await fetch(`${API}/api/ki-ueberwachung/entwuerfe/${id}/freigeben`, { method: 'POST', headers: headers() });
    toast.success('Entwurf freigegeben');
    fetchAll();
  };

  const handleAblehnen = async (id) => {
    await fetch(`${API}/api/ki-ueberwachung/entwuerfe/${id}/ablehnen`, { method: 'POST', headers: headers() });
    toast.success('Entwurf abgelehnt');
    fetchAll();
  };

  const handleDeleteEntwurf = async (id) => {
    if (!window.confirm('Entwurf wirklich löschen?')) return;
    await fetch(`${API}/api/ki-ueberwachung/entwuerfe/${id}`, { method: 'DELETE', headers: headers() });
    fetchAll();
  };

  const handleSaveEntwurf = async (id, inhalt) => {
    await fetch(`${API}/api/ki-ueberwachung/entwuerfe/${id}`, {
      method: 'PUT', headers: headers(), body: JSON.stringify({ inhalt }),
    });
    setEditingEntwurf(null);
    toast.success('Entwurf gespeichert');
    fetchAll();
  };

  const handleCreateVorlage = async (e) => {
    e.preventDefault();
    if (!newVorlage.titel || !newVorlage.inhalt) { toast.error('Titel und Inhalt erforderlich'); return; }
    await fetch(`${API}/api/ki-ueberwachung/vorlagen`, {
      method: 'POST', headers: headers(), body: JSON.stringify(newVorlage),
    });
    setNewVorlage({ titel: '', kategorie: '', inhalt: '', tags: '' });
    setShowNewVorlage(false);
    toast.success('Vorlage erstellt');
    fetchAll();
  };

  const handleDeleteVorlage = async (id) => {
    if (!window.confirm('Vorlage wirklich löschen?')) return;
    await fetch(`${API}/api/ki-ueberwachung/vorlagen/${id}`, { method: 'DELETE', headers: headers() });
    fetchAll();
  };

  const d = dashboard || {};

  return (
    <div className="space-y-6" data-testid="ki-ueberwachung-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Brain className="w-7 h-7 text-violet-600" />
            KI-Fristenüberwachung
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Proaktive Überwachung &middot; Automatische Aufgaben &middot; Antragsentwürfe
          </p>
        </div>
        <Button onClick={handleTrigger} disabled={triggering} className="bg-violet-600 hover:bg-violet-700" data-testid="ki-trigger-btn">
          {triggering ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
          Jetzt prüfen
        </Button>
      </div>

      {/* Status-Karten */}
      <div className="grid gap-4 md:grid-cols-4">
        {[
          { label: 'Erinnerungen', value: d.erinnerungen_ungelesen || 0, icon: Bell, color: 'text-amber-600 bg-amber-100' },
          { label: 'Entwürfe offen', value: d.entwuerfe_offen || 0, icon: FileText, color: 'text-blue-600 bg-blue-100' },
          { label: 'Vorlagen', value: d.vorlagen_count || 0, icon: BookTemplate, color: 'text-violet-600 bg-violet-100' },
          { label: 'Heute erstellt', value: d.heute_aufgaben || 0, icon: CheckCircle, color: 'text-green-600 bg-green-100' },
        ].map(s => (
          <Card key={s.label}>
            <CardContent className="p-4 flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${s.color}`}>
                <s.icon className="w-5 h-5" />
              </div>
              <div>
                <p className="text-2xl font-bold">{s.value}</p>
                <p className="text-xs text-slate-500">{s.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {d.letzter_lauf && (
        <div className="text-xs text-slate-400 flex items-center gap-2">
          <Clock className="w-3 h-3" />
          Letzter Durchlauf: {new Date(d.letzter_lauf.timestamp).toLocaleString('de-DE')}
          &middot; {d.letzter_lauf.aufgaben_erstellt} Aufgaben
          &middot; {d.letzter_lauf.entwuerfe_erstellt} Entwürfe
          &middot; {d.letzter_lauf.erinnerungen} Erinnerungen
          {d.letzter_lauf.fehler?.length > 0 && <Badge variant="destructive" className="text-[10px]">{d.letzter_lauf.fehler.length} Fehler</Badge>}
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4" data-testid="ki-tabs">
          <TabsTrigger value="dashboard" data-testid="ki-tab-dashboard">
            <Bell className="w-4 h-4 mr-1" /> Erinnerungen
          </TabsTrigger>
          <TabsTrigger value="entwuerfe" data-testid="ki-tab-entwuerfe">
            <FileText className="w-4 h-4 mr-1" /> Entwürfe
          </TabsTrigger>
          <TabsTrigger value="vorlagen" data-testid="ki-tab-vorlagen">
            <BookTemplate className="w-4 h-4 mr-1" /> Vorlagen
          </TabsTrigger>
          <TabsTrigger value="log" data-testid="ki-tab-log">
            <Clock className="w-4 h-4 mr-1" /> Protokoll
          </TabsTrigger>
        </TabsList>

        {/* === ERINNERUNGEN === */}
        <TabsContent value="dashboard">
          <Card>
            <CardHeader className="flex-row items-center justify-between">
              <CardTitle className="text-lg">KI-Erinnerungen</CardTitle>
              {erinnerungen.filter(e => !e.gelesen).length > 0 && (
                <Button size="sm" variant="outline" onClick={handleAlleGelesen} data-testid="ki-mark-all-read">
                  <CheckCircle className="w-3.5 h-3.5 mr-1" /> Alle gelesen
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {erinnerungen.length === 0 ? (
                <div className="text-center py-8 text-slate-400 text-sm">
                  <Bell className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  Keine Erinnerungen vorhanden
                </div>
              ) : (
                <ScrollArea className="max-h-[500px]">
                  <div className="space-y-2">
                    {erinnerungen.map(e => (
                      <div key={e.id} data-testid={`ki-erinnerung-${e.id}`}
                        className={`p-3 rounded-lg border flex items-start gap-3 ${e.gelesen ? 'opacity-50 bg-slate-50' : 'bg-white border-amber-200'}`}>
                        <AlertTriangle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                          e.dringlichkeit === 'kritisch' ? 'text-red-500' : e.dringlichkeit === 'hoch' ? 'text-amber-500' : 'text-blue-500'
                        }`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-sm">{e.titel}</p>
                            <Badge variant={e.dringlichkeit === 'kritisch' ? 'destructive' : 'secondary'} className="text-[10px]">
                              {e.dringlichkeit}
                            </Badge>
                          </div>
                          <p className="text-xs text-slate-600 mt-1">{e.nachricht}</p>
                          <p className="text-[10px] text-slate-400 mt-1">{e.datum && new Date(e.datum).toLocaleString('de-DE')}</p>
                        </div>
                        {!e.gelesen && (
                          <Button size="icon" variant="ghost" className="w-7 h-7 flex-shrink-0" onClick={() => handleMarkGelesen(e.id)}>
                            <Eye className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* === ENTWÜRFE === */}
        <TabsContent value="entwuerfe">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Antragsentwürfe</CardTitle>
            </CardHeader>
            <CardContent>
              {entwuerfe.length === 0 ? (
                <div className="text-center py-8 text-slate-400 text-sm">
                  <FileText className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  Keine Entwürfe vorhanden
                  <p className="text-xs mt-1">Claude erstellt Entwürfe automatisch wenn Fristen anstehen</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {entwuerfe.map(ent => (
                    <div key={ent.id} className="border rounded-lg" data-testid={`ki-entwurf-${ent.id}`}>
                      <div className="p-3 flex items-center justify-between cursor-pointer hover:bg-slate-50"
                        onClick={() => setExpandedItem(expandedItem === ent.id ? null : ent.id)}>
                        <div className="flex items-center gap-3">
                          <FileText className="w-5 h-5 text-blue-500" />
                          <div>
                            <p className="font-medium text-sm">{ent.titel}</p>
                            <p className="text-xs text-slate-500">
                              {ent.klient_name} &middot; {ent.empfaenger || 'k.A.'} &middot; {ent.typ}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant={ent.status === 'entwurf' ? 'secondary' : ent.status === 'freigegeben' ? 'default' : 'destructive'}
                            className="text-[10px]">{ent.status}</Badge>
                          {expandedItem === ent.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </div>
                      </div>
                      {expandedItem === ent.id && (
                        <div className="px-3 pb-3 border-t">
                          {ent.betreff && <p className="text-sm font-medium mt-2">Betreff: {ent.betreff}</p>}
                          {editingEntwurf === ent.id ? (
                            <div className="mt-2">
                              <textarea className="w-full border rounded-lg p-3 text-sm min-h-[200px] focus:outline-none focus:ring-2 focus:ring-blue-500"
                                defaultValue={ent.inhalt}
                                id={`entwurf-text-${ent.id}`} />
                              <div className="flex gap-2 mt-2">
                                <Button size="sm" onClick={() => handleSaveEntwurf(ent.id, document.getElementById(`entwurf-text-${ent.id}`).value)}>
                                  <Save className="w-3.5 h-3.5 mr-1" /> Speichern
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => setEditingEntwurf(null)}>Abbrechen</Button>
                              </div>
                            </div>
                          ) : (
                            <pre className="text-xs whitespace-pre-wrap bg-slate-50 rounded p-3 mt-2 max-h-[300px] overflow-y-auto">{ent.inhalt}</pre>
                          )}
                          {ent.ki_notiz && (
                            <div className="mt-2 p-2 bg-amber-50 rounded border border-amber-100 text-xs text-amber-800">
                              <Brain className="w-3 h-3 inline mr-1" />KI-Notiz: {ent.ki_notiz}
                            </div>
                          )}
                          <Separator className="my-2" />
                          <div className="flex gap-2">
                            {ent.status === 'entwurf' && (
                              <>
                                <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => handleFreigeben(ent.id)}>
                                  <Send className="w-3.5 h-3.5 mr-1" /> Freigeben
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => setEditingEntwurf(ent.id)}>
                                  <Edit3 className="w-3.5 h-3.5 mr-1" /> Bearbeiten
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => handleAblehnen(ent.id)}>
                                  <XCircle className="w-3.5 h-3.5 mr-1" /> Ablehnen
                                </Button>
                              </>
                            )}
                            <Button size="sm" variant="destructive" onClick={() => handleDeleteEntwurf(ent.id)}>
                              <Trash2 className="w-3.5 h-3.5 mr-1" /> Löschen
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* === VORLAGEN === */}
        <TabsContent value="vorlagen">
          <Card>
            <CardHeader className="flex-row items-center justify-between">
              <CardTitle className="text-lg">Antragsvorlagen</CardTitle>
              <Button size="sm" onClick={() => setShowNewVorlage(!showNewVorlage)} data-testid="ki-add-vorlage-btn">
                <Plus className="w-4 h-4 mr-1" /> Vorlage hinzufügen
              </Button>
            </CardHeader>
            <CardContent>
              {showNewVorlage && (
                <form onSubmit={handleCreateVorlage} className="mb-4 p-4 border-2 border-dashed border-violet-200 rounded-lg bg-violet-50/30">
                  <h4 className="font-medium text-sm mb-3">Neue Vorlage</h4>
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div>
                      <label className="text-xs font-medium">Titel *</label>
                      <Input value={newVorlage.titel} onChange={e => setNewVorlage(v => ({ ...v, titel: e.target.value }))}
                        placeholder="z.B. Grundsicherung Weiterbewilligung" className="h-8 text-xs" data-testid="ki-vorlage-titel" />
                    </div>
                    <div>
                      <label className="text-xs font-medium">Kategorie</label>
                      <Input value={newVorlage.kategorie} onChange={e => setNewVorlage(v => ({ ...v, kategorie: e.target.value }))}
                        placeholder="grundsicherung, rente, jobcenter..." className="h-8 text-xs" />
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="text-xs font-medium">Vorlagetext *</label>
                    <textarea className="w-full border rounded-lg p-3 text-xs min-h-[120px] focus:outline-none focus:ring-2 focus:ring-violet-500"
                      value={newVorlage.inhalt} onChange={e => setNewVorlage(v => ({ ...v, inhalt: e.target.value }))}
                      placeholder="Brieftext mit Platzhaltern: [Name], [Geburtsdatum], [AZ]..." data-testid="ki-vorlage-inhalt" />
                  </div>
                  <div className="mb-3">
                    <label className="text-xs font-medium">Tags (kommagetrennt)</label>
                    <Input value={newVorlage.tags} onChange={e => setNewVorlage(v => ({ ...v, tags: e.target.value }))}
                      placeholder="grundsicherung,sgb xii,sozialamt" className="h-8 text-xs" />
                  </div>
                  <div className="flex gap-2">
                    <Button type="submit" size="sm" data-testid="ki-vorlage-save-btn">
                      <Save className="w-3.5 h-3.5 mr-1" /> Speichern
                    </Button>
                    <Button type="button" variant="ghost" size="sm" onClick={() => setShowNewVorlage(false)}>Abbrechen</Button>
                  </div>
                </form>
              )}

              {vorlagen.length === 0 && !showNewVorlage ? (
                <div className="text-center py-8 text-slate-400 text-sm">
                  <BookTemplate className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  Noch keine Vorlagen
                  <p className="text-xs mt-1">Claude nutzt Vorlagen als Grundlage für Antragsentwürfe</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {vorlagen.map(v => (
                    <div key={v.id} className="border rounded-lg" data-testid={`ki-vorlage-${v.id}`}>
                      <div className="p-3 flex items-center justify-between cursor-pointer hover:bg-slate-50"
                        onClick={() => setExpandedItem(expandedItem === v.id ? null : v.id)}>
                        <div className="flex items-center gap-3">
                          <BookTemplate className="w-5 h-5 text-violet-500" />
                          <div>
                            <p className="font-medium text-sm">{v.titel}</p>
                            <p className="text-xs text-slate-500">
                              {v.kategorie} &middot; {v.nutzungen || 0}x verwendet
                              {v.letzte_nutzung && ` &middot; Zuletzt: ${new Date(v.letzte_nutzung).toLocaleDateString('de-DE')}`}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {v.tags && <span className="text-[10px] text-slate-400">{v.tags}</span>}
                          {expandedItem === v.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </div>
                      </div>
                      {expandedItem === v.id && (
                        <div className="px-3 pb-3 border-t">
                          <pre className="text-xs whitespace-pre-wrap bg-slate-50 rounded p-3 mt-2 max-h-[250px] overflow-y-auto">{v.inhalt}</pre>
                          <div className="flex gap-2 mt-2">
                            <Button size="sm" variant="destructive" onClick={() => handleDeleteVorlage(v.id)}>
                              <Trash2 className="w-3.5 h-3.5 mr-1" /> Löschen
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* === PROTOKOLL === */}
        <TabsContent value="log">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Überwachungs-Protokoll</CardTitle>
            </CardHeader>
            <CardContent>
              {logs.length === 0 ? (
                <div className="text-center py-8 text-slate-400 text-sm">
                  <Clock className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  Noch keine Durchläufe
                  <p className="text-xs mt-1">Die KI-Überwachung läuft täglich um 8:05 Uhr</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {logs.map((log, i) => (
                    <div key={log.id || i} className="p-3 rounded-lg border bg-slate-50 text-sm">
                      <div className="flex items-center justify-between">
                        <p className="font-medium">{log.timestamp && new Date(log.timestamp).toLocaleString('de-DE')}</p>
                        <div className="flex gap-2">
                          {log.aufgaben_erstellt > 0 && <Badge className="text-[10px] bg-green-600">{log.aufgaben_erstellt} Aufgaben</Badge>}
                          {log.entwuerfe_erstellt > 0 && <Badge className="text-[10px] bg-blue-600">{log.entwuerfe_erstellt} Entwürfe</Badge>}
                          {log.erinnerungen > 0 && <Badge className="text-[10px] bg-amber-600">{log.erinnerungen} Erinnerungen</Badge>}
                          {log.fehler?.length > 0 && <Badge variant="destructive" className="text-[10px]">{log.fehler.length} Fehler</Badge>}
                        </div>
                      </div>
                      {log.details?.length > 0 && (
                        <ul className="text-xs text-slate-600 mt-1 list-disc list-inside">
                          {log.details.map((d, j) => <li key={j}>{d}</li>)}
                        </ul>
                      )}
                      {log.fehler?.length > 0 && (
                        <div className="text-xs text-red-600 mt-1">Fehler: {log.fehler.join(', ')}</div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
